import { ReactNode } from "react"
import {
  Dialog as HeadlessDialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from "@headlessui/react"
import { clsx } from "clsx"

import { IconButton } from "@src/components/atoms/IconButton"

export type DialogProps = {
  title?: string
  open: boolean
  onClose: () => void
  children: ReactNode
  actions?: ReactNode
  size?: "small" | "medium" | "large" | "xlarge"
  dismissable?: boolean
  fullHeight?: boolean
  /** Render children edge-to-edge without padding, managing their own scroll */
  flush?: boolean
}

export const Dialog = ({
  title,
  open,
  onClose,
  children,
  actions,
  size = "medium",
  dismissable = true,
  fullHeight = false,
  flush = false,
}: DialogProps) => {
  return (
    <HeadlessDialog
      open={open}
      onClose={dismissable ? onClose : () => {}}
      className="fixed inset-0 z-50"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />

      <DialogPanel
        className={clsx([
          "fixed inset-0 w-full bg-white shadow-lg",
          "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-10/12 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg",
          fullHeight
            ? "flex flex-col overflow-hidden sm:h-[85dvh]"
            : "overflow-y-auto sm:h-auto sm:max-h-[90dvh]",
          size === "small" && "lg:w-1/2 lg:min-w-[520px] lg:max-w-[520px]",
          size === "medium" && "lg:w-1/2 lg:min-w-[800px] lg:max-w-[800px]",
          size === "large" && "lg:w-3/4",
          size === "xlarge" && "lg:w-4/5 lg:max-w-[1100px]",
        ])}
      >
        {title && (
          <DialogTitle className="flex items-center justify-between border-b border-pearl-lighter p-4 text-paragraph font-semibold text-black-light">
            {title}
          </DialogTitle>
        )}

        {dismissable && (
          <IconButton
            icon="close"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 text-paragraph"
            variant="ternary"
          />
        )}

        <div className={clsx(fullHeight && "flex min-h-0 flex-1 flex-col")}>
          {children && (
            <div
              className={clsx(
                !flush && "p-4",
                fullHeight && "min-h-0 flex-1",
                fullHeight && (flush ? "overflow-hidden" : "overflow-y-auto"),
              )}
            >
              {children}
            </div>
          )}
          {actions && (
            <div className="bg-pearl-lighter/15 px-4 py-2">{actions}</div>
          )}
        </div>
      </DialogPanel>
    </HeadlessDialog>
  )
}
